import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { AppConfigService } from '~/core/services/appconfig.service';
import { HttpService } from '~/core/services/http.service';

/**
 * Message types received from the backend WebSocket.
 *
 * Flow: status → sql → answer → complete
 * Keepalive: ping (server) → pong (client)
 */
export interface WsMessage {
  type: 'status' | 'sql' | 'answer' | 'complete' | 'ping' | 'pong' | 'error' | 'auth_success' | 'auth_failed' | 'clarification';
  data?: any;
}

@Injectable({ providedIn: 'root' })
export class WebSocketService implements OnDestroy {
  private ws: WebSocket | null = null;
  private sessionId: string = '';
  private reconnectTimer: any = null;
  private pingPongTimer: any = null;

  /** Emits every message received from the server */
  private messageSubject = new Subject<WsMessage>();
  messages$: Observable<WsMessage> = this.messageSubject.asObservable();

  /** Emits true on open, false on close */
  private connectionSubject = new Subject<boolean>();
  connection$: Observable<boolean> = this.connectionSubject.asObservable();

  /** Enable detailed logging for visual testing */
  private DEBUG = true;

  constructor(
    private appConfigService: AppConfigService,
    private httpService: HttpService
  ) {}

  get isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  get activeSessionId(): string {
    return this.sessionId;
  }

  private log(level: 'info' | 'warn' | 'error' | 'debug' | 'success', message: string, data?: any): void {
    if (!this.DEBUG && level === 'debug') return;

    const timestamp = new Date().toLocaleTimeString();
    const icons: { [key: string]: string } = {
      'info': 'ℹ️',
      'success': '✅',
      'warn': '⚠️',
      'error': '❌',
      'debug': '📋',
    };

    const icon = icons[level] || 'ℹ️';
   

    
  }

  /**
   * Open a WebSocket connection for the given session.
   * If already connected with a different session, the old connection is closed first.
   */
  connect(sessionId: string): void {
    if (this.ws && this.sessionId === sessionId && this.ws.readyState === WebSocket.OPEN) {
      this.log('info', `Already connected to session ${sessionId.substring(0, 8)}...`);
      return; // already connected to this session
    }
    this.disconnect(); // close any previous connection

    this.sessionId = sessionId;
    const wsBaseUrl = this.appConfigService.getConfig('wssUrl');
    
    // Fallback if config not loaded
    if (!wsBaseUrl) {
      this.log('error', 'WebSocket URL not configured. Config may not have loaded.');
      this.log('error', 'Available config:', this.appConfigService.getConfig());
      return;
    }
    
    const wsEndpoint = `/api/v1/ws/query/${sessionId}`;
    const url = this.appConfigService.concatenateUrl(wsBaseUrl, wsEndpoint);

    this.log('info', `Connecting to ${url}`);

    this.ws = new WebSocket(url);

    this.ws.onopen = () => {
      this.log('success', `Connected | session=${sessionId.substring(0, 8)}...`);
      // JWT token will be sent with the first question message via sendQuestion()
      this.connectionSubject.next(true);
    };

    this.ws.onmessage = (event: MessageEvent) => {
      try {
        const msg: WsMessage = JSON.parse(event.data);

        // Auto-reply to keepalive pings
        if (msg.type === 'ping') {
          this.log('debug', '📡 Keepalive PING received from server');
          this.sendRaw({ type: 'pong' });
          this.log('debug', '📡 Keepalive PONG sent to server ✅');
          return; // don't propagate pings to consumers
        }

        // Log incoming messages
        const dataPreview = typeof msg.data === 'string' ? msg.data.substring(0, 50) : JSON.stringify(msg.data).substring(0, 50);
        this.log('info', `📥 Message received: {type: "${msg.type}", data: "${dataPreview}${msg.data && msg.data.length > 50 ? '...' : ''}"}`);

        this.messageSubject.next(msg);
      } catch (e) {
        this.log('error', 'Failed to parse message', event.data);
      }
    };

    this.ws.onerror = (error) => {
      this.log('error', 'WebSocket error', error);
    };

    this.ws.onclose = (event) => {
      this.log('warn', `Disconnected | code=${event.code} reason=${event.reason}`);
      this.connectionSubject.next(false);
      // Auto-reconnect if the close was not intentional (code !== 1000)
      if (event.code !== 1000) {
        this.scheduleReconnect();
      }
    };
  }

  /** Send a question through the WebSocket, including JWT token in the same message */
  sendQuestion(question: string, wellIds?: string[]): void {
    this.log('info', `📤 User sent question: "${question.substring(0, 50)}${question.length > 50 ? '...' : ''}"`);
    const jwtToken = this.httpService.getWellsToken();
    const payload: any = { question };
    
    // Include well_ids if provided (for multiwell first question)
    if (wellIds && wellIds.length > 0) {
      payload.well_ids = wellIds;
      this.log('info', `📤 Including well_ids (${wellIds.length} wells)`);
    }
    
    // Include JWT token if available
    if (jwtToken) {
      payload.jwt_token = jwtToken;
    }
    
    this.sendRaw(payload);
  }

  /** Send any JSON payload */
  private sendRaw(payload: any): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(payload));
    } else {
      this.log('warn', 'Cannot send — WebSocket not open');
    }
  }

  /** Gracefully close the connection */
  disconnect(): void {
    this.clearReconnect();
    if (this.ws) {
      this.ws.onclose = null; // prevent auto-reconnect
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
      this.connectionSubject.next(false);
      this.log('debug', 'Disconnected gracefully');
    }
  }

  /** Schedule an auto-reconnect after 3 seconds */
  private scheduleReconnect(): void {
    this.clearReconnect();
    this.log('info', '🔄 Scheduling reconnect in 3 seconds...');
    this.reconnectTimer = setTimeout(() => {
      this.log('info', '🔄 Attempting reconnect…');
      this.connect(this.sessionId);
    }, 3000);
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  ngOnDestroy(): void {
    this.disconnect();
    this.messageSubject.complete();
    this.connectionSubject.complete();
  }
}
